import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Col, Form, Grid, Input, Modal, Row, Select, Space, Tabs, Typography } from 'antd'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import {
  DELIVERY_METHODS,
  LOGIN_PATTERN,
  PHONE_E164,
  createSubscriber,
  fetchSubscriberContact,
  normalizePhone,
  sendContact,
  updateSubscriber,
  type DeliveryMethod,
  type Subscriber,
  type SubscriberContact,
  type SubscriberDraft,
} from './subscriberApi'
import {
  CONTACT_EMAIL_SHAPE,
  CONTACT_LIMITS,
  CONTACT_PHONE_FORBIDDEN,
  CONTACT_PHONE_MAX,
  CONTACT_PHONE_MIN,
  contactRequest,
  isMaskedValue,
  type ExistingContact,
} from './contactDraft'
import {
  contactGeoKeys,
  fetchDistrictOptions,
  fetchRegionOptions,
  type GeoOption,
} from './contactGeo'
import { subscriberKeys } from './subscriberKeys'
import { SubscriberProfileFields } from './SubscriberProfileFields'
import { profileDraft, profileFormValues, type ProfileFieldValues } from './profileForm'
import {
  REVEAL_REASON_MAX,
  revealSubscriberContact,
  type ContactReveal,
} from '@/shared/api/contactReveal'
import { PERM } from '@/shared/auth/partnerPermissions'
import { useAuth } from '@/shared/auth/useAuth'

interface FormValues {
  login: string
  password?: string
  displayName?: string
  phone?: string
  kind: DeliveryMethod
  fullName?: string
  contactPhone?: string
  phoneAlt?: string
  email?: string
  regionId?: string
  districtId?: string
  address?: string
  note?: string
  profile?: ProfileFieldValues
}

/**
 * «Ulanish» bo'limining maydonlari — xato qaysi bo'limda ekanini
 * aniqlash uchun. Qolgan hammasi kontakt bo'limida.
 */
const ACCESS_FIELDS: readonly string[] = ['login', 'password', 'displayName', 'phone', 'kind']

const EMPTY_VALUES: FormValues = {
  login: '',
  password: '',
  displayName: '',
  phone: '',
  kind: 'PPPOE',
  fullName: '',
  contactPhone: '',
  phoneAlt: '',
  email: '',
  regionId: undefined,
  districtId: undefined,
  address: '',
  note: '',
}

function contactValuesOf(contact: SubscriberContact): Partial<FormValues> {
  return {
    fullName: contact.fullName ?? '',
    contactPhone: contact.phone ?? '',
    phoneAlt: contact.phoneAlt ?? '',
    email: contact.email ?? '',
    regionId: contact.regionId ?? undefined,
    districtId: contact.districtId ?? undefined,
    address: contact.address ?? '',
    note: contact.note ?? '',
  }
}

/**
 * Reestrdan kelmagan, lekin kontaktda TURGAN viloyat/tuman.
 *
 * ⚠️ `geo.read` yopiq bo'lsa yoki ro'yxat qisqartirilgan bo'lsa mos
 * tanlov topilmaydi va antd maydonda XOM UUID ko'rsatardi. Nom
 * `ContactEntry` ning o'zidan keladi.
 */
function fallbackOption(id: string | null, name: string | null): GeoOption | null {
  return id === null ? null : { id, name: name ?? id }
}

function mergeOptions(
  options: readonly GeoOption[],
  fallback: GeoOption | null,
): readonly GeoOption[] {
  if (fallback === null || options.some((option) => option.id === fallback.id)) return options
  return [fallback, ...options]
}

interface SubscriberFormModalProps {
  open: boolean
  /** `null` — yangi abonent; aks holda tahrirlash */
  subscriber: Subscriber | null
  onClose: () => void
}

/**
 * Yaratish va tahrirlash — BITTA forma.
 *
 * <h2>⚠️ Nega ikkita modal emas (admin panelida shunday)</h2>
 * Maydonlar to'plami bir xil bo'lgach, ikki fayl ikki joyda
 * tuzatiladigan bitta nuqson demak: telefon naqshi yoki login
 * tekshiruvi biridan ikkinchisiga o'tmay qolardi. Farq atigi ikkita va
 * ikkalasi ham SABABLI, quyida.
 *
 * <h2>⚠️ NEGA BO'LIMLAR (`Tabs`), UZUN RO'YXAT EMAS</h2>
 * Kontakt bilan birga maydonlar soni beshtadan o'n uchtaga chiqdi.
 * Telefonda modal TO'LIQ EKRAN (`styles/global.css`), ya'ni o'n uchta
 * maydon ikki-uch ekranlik aylantirish demak va «Saqlash» tugmasi
 * eng pastda qolardi — operator uni har safar qidirib topardi.
 * Ikki bo'lim har birini bitta ekranga sig'diradi va tugmalar
 * O'RNIDAN QIMIRLAMAYDI.
 *
 * ⚠️ QADAMLAR (`Steps`) ATAYLAB EMAS: kontakt IXTIYORIY, qadam esa
 * uni majburiy bosqichga aylantirardi — «keyingi» tugmasini bosmasdan
 * abonent yaratib bo'lmasdi. Bo'limda esa kontaktga umuman tegmay
 * saqlash mumkin.
 *
 * ⚠️ YASHIRIN BO'LIMDAGI XATO — `onFinishFailed` bo'limni O'ZI ochadi.
 * Usiz «Saqlash» hech narsa qilmayotgandek ko'rinardi: xato bor,
 * lekin u ko'rinmaydigan bo'limda.
 *
 * ⚠️ MAYDONLAR RO'YXATI QISQARTIRILGAN — nima olinmagani va NEGA
 * `subscriberApi.ts` dagi `SubscriberDraft` izohida.
 */
export function SubscriberFormModal({ open, subscriber, onClose }: SubscriberFormModalProps) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false

  const [saving, setSaving] = useState(false)
  const [errorText, setErrorText] = useState<string | null>(null)
  const [contactErrorText, setContactErrorText] = useState<string | null>(null)
  /** Abonent SAQLANDI — takror urinishda u qayta yuborilmasin */
  const [savedId, setSavedId] = useState<string | null>(null)
  const [tab, setTab] = useState('access')

  const isCreate = subscriber === null
  const subscriberId = subscriber?.id ?? null

  const contactQuery = useQuery({
    queryKey: subscriberKeys.contact(subscriberId ?? ''),
    queryFn: ({ signal }) => fetchSubscriberContact(subscriberId ?? '', signal),
    enabled: open && subscriberId !== null,
  })

  const contact = contactQuery.data ?? null

  // ⛔ Oyna yopilganda ochilgan raqam YO'QOLADI. Usiz u keyingi
  //    abonentning formasida ham turib qolardi — boshqa odamning
  //    raqami, ochilgan holda.
  useEffect(() => {
    if (!open) setRevealed(null)
  }, [open])

  /**
   * ⚠️ Niqoblangan raqam bilan tahrir OCHILMAYDI.
   *
   * `PUT /api/subscribers/{id}/contact` TO'LIQ ALMASHTIRISH: `+9989****567` ni
   * qaytarib yuborish haqiqiy raqamni yulduzchalar bilan bosib
   * yozardi va hech qanday xato chiqmasdi — nosozlik faqat SMS
   * bormaganda bilinardi.
   */
  /**
   * Serverdan OCHILGAN to'liq raqamlar; `null` — hali ochilmagan.
   *
   * ⛔ Kesh YO'Q va bo'lmaydi (`revealSubscriberContact` izohi): qiymat
   * shu oynaning holatida yashaydi va oyna yopilganda yo'qoladi.
   */
  const [revealed, setRevealed] = useState<ContactReveal | null>(null)

  /**
   * ⛔ NIQOBLANGAN RAQAM BILAN TAHRIR OCHILMAYDI — va bu ikki tomonlama
   * himoya, chunki HAR IKKALA yo'l ham raqamni YO'Q QILADI (o'lchandi
   * 2026-08-24, `ContactRepository.update:290-304`):
   *
   *   · niqobni QAYTARIB YUBORISH — `+9989****567` bazaga AYNAN shu
   *     holda yoziladi: `ContactPhoneRule` yulduzchani taqiqlamaydi va
   *     `contact_phone_shape` CHECK ham (E.164 talabi olib tashlangan);
   *   · maydonni YUBORMASLIK — `PUT` TO'LIQ ALMASHTIRISH va
   *     `SET phone = :phone` shartsiz bajariladi, ya'ni yo'q maydon
   *     `NULL` bo'lib yoziladi.
   *
   * Ya'ni «niqoblangan qiymatni tashlab yuborish» YETARLI EMAS edi.
   * Yagona to'g'ri yo'l — HAQIQIY qiymatni olish, va u faqat auditli
   * ochish orqali keladi.
   *
   * ⚠️ Ochilgandan keyin qulf TUSHADI: forma endi haqiqiy raqamlar
   * bilan to'lgan va `PUT` ularni o'zgarishsiz qaytaradi.
   */
  const maskedContact =
    contact !== null && (isMaskedValue(contact.phone) || isMaskedValue(contact.phoneAlt))
  const locked = maskedContact && revealed === null

  const [revealReason, setRevealReason] = useState('')
  const [revealing, setRevealing] = useState(false)
  const [revealError, setRevealError] = useState<unknown>(null)
  const canReveal = can(PERM.contactReveal)

  /**
   * To'liq raqamlarni oladi va ULARNI FORMAGA yozadi.
   *
   * ⚠️ Maydonlarni to'ldirish MAJBURIY qism: `PUT` to'liq
   * almashtirish, ya'ni saqlashda forma NIMA tursa O'SHA yoziladi.
   * Faqat qulfni ochib, maydonlarni niqobli qoldirish eng yomon
   * variant bo'lardi — operator «ochdim» deb saqlab, raqamni
   * yulduzchalar bilan bosib yozardi.
   */
  const doReveal = () => {
    if (subscriber === null) return
    setRevealing(true)
    setRevealError(null)
    revealSubscriberContact(subscriber.id, revealReason)
      .then((full) => {
        setRevealed(full)
        // ⛔ KONTAKT maydonlari — `contactPhone`/`contactPhoneAlt`.
        //    Javobdagi `phone` bu ABONENT yozuvining raqami (SMS
        //    manbasi) va uni kontakt formasiga yozish boshqa raqamni
        //    kontakt sifatida SAQLAB qo'yardi.
        form.setFieldsValue({
          contactPhone: full.contactPhone ?? '',
          phoneAlt: full.contactPhoneAlt ?? '',
        })
        setRevealReason('')
      })
      .catch((cause: unknown) => setRevealError(cause))
      .finally(() => setRevealing(false))
  }

  const existing: ExistingContact | null =
    contact === null ? null : { id: contact.id, locked }

  const regionQuery = useQuery({
    queryKey: contactGeoKeys.regions,
    queryFn: ({ signal }) => fetchRegionOptions(signal),
    enabled: open,
  })

  const regionId = Form.useWatch('regionId', form)

  const districtQuery = useQuery({
    queryKey: contactGeoKeys.districts(regionId ?? ''),
    queryFn: ({ signal }) => fetchDistrictOptions(regionId ?? '', signal),
    enabled: open && typeof regionId === 'string' && regionId !== '',
  })

  const geoForbidden = regionQuery.data?.forbidden === true
  const geoTruncated =
    regionQuery.data?.truncated === true || districtQuery.data?.truncated === true

  const regionOptions = useMemo(
    () =>
      mergeOptions(
        regionQuery.data?.options ?? [],
        fallbackOption(contact?.regionId ?? null, contact?.regionName ?? null),
      ),
    [regionQuery.data, contact],
  )

  const districtOptions = useMemo(
    () =>
      mergeOptions(
        districtQuery.data?.options ?? [],
        fallbackOption(contact?.districtId ?? null, contact?.districtName ?? null),
      ),
    [districtQuery.data, contact],
  )

  useEffect(() => {
    if (!open) return
    setErrorText(null)
    setContactErrorText(null)
    setSavedId(null)
    setTab('access')
    form.setFieldsValue({
      ...EMPTY_VALUES,
      login: subscriber?.login ?? '',
      displayName: subscriber?.displayName ?? '',
      phone: subscriber?.phone ?? '',
      kind: subscriber?.kind ?? 'PPPOE',
      profile: profileFormValues(subscriber?.profile),
    })
  }, [open, subscriber, form])

  /*
    ⚠️ Kontakt KEYIN keladi va formaga alohida qo'yiladi.

    Yuqoridagi effekt modal ochilishi bilan ishlaydi, kontakt esa hali
    yo'lda. Ikkalasini birlashtirish kontakt yetib kelganda operator
    yozib ulgurgan matnni bosib yozardi — shuning uchun bu effekt faqat
    javob KELGANDA va faqat kontakt maydonlariga tegadi.
  */
  useEffect(() => {
    if (!open || contact === null) return
    form.setFieldsValue(contactValuesOf(contact))
  }, [open, contact, form])

  const submit = (values: FormValues) => {
    const draft: SubscriberDraft = {
      login: values.login.trim(),
      // ⚠️ Bo'sh maydon `null` bo'lib ketadi, `""` emas: bo'sh satr
      // bazada «ismi bor, lekin bo'sh» bo'lib yozilardi va ro'yxatda
      // ism ustuni ko'rinmas holda «to'ldirilgan» bo'lib qolardi.
      displayName: showDisplayName ? values.displayName?.trim() || null : subscriber?.displayName ?? null,
      phone: normalizePhone(values.phone),
      kind: values.kind,
      ...(isCreate && values.password ? { password: values.password } : {}),
      profile: profileDraft(values.profile ?? {}),
    }

    setSaving(true)
    setErrorText(null)
    setContactErrorText(null)

    const saveSubscriber = (): Promise<string> => {
      if (savedId !== null) return Promise.resolve(savedId)
      if (isCreate) return createSubscriber(draft).then((created) => created.id)
      return updateSubscriber(subscriber.id, draft).then((updated) => updated.id)
    }

    saveSubscriber()
      .then((id) => {
        void queryClient.invalidateQueries({ queryKey: subscriberKeys.all })

        const request = contactRequest({
          subscriberId: id,
          values: {
            fullName: values.fullName,
            phone: values.contactPhone,
            phoneAlt: values.phoneAlt,
            email: values.email,
            regionId: values.regionId,
            districtId: values.districtId,
            address: values.address,
            note: values.note,
          },
          existing,
        })

        if (request === null) {
          onClose()
          return
        }

        /*
          ⚠️ YARIM HOLAT YASHIRILMAYDI (SHARTNOMA-4 §A2).

          Abonent `xsib-subscriber` da, kontakt `xsib-platform` da —
          bitta tranzaksiya YO'Q (ADR-016). Abonent saqlanib, kontakt
          saqlanmasligi MUMKIN va bu holat operatorga AYTILADI: modal
          yopilmaydi, ogohlantirish qoladi va faqat kontakt qayta
          yuboriladi (`savedId` abonentni ikkinchi marta yaratishdan
          saqlaydi).
        */
        return sendContact(request)
          .then(() => {
            void queryClient.invalidateQueries({ queryKey: subscriberKeys.contact(id) })
            onClose()
          })
          .catch((error: unknown) => {
            setSavedId(id)
            setContactErrorText(translateError(error, t))
            setTab('contact')
          })
      })
      .catch((error: unknown) => {
        setErrorText(translateError(error, t))
        setTab('access')
      })
      .finally(() => {
        setSaving(false)
      })
  }

  const contactDisabled = locked
  const showDisplayName = !isCreate && !subscriber?.profile?.firstName && !subscriber?.profile?.lastName

  const accessTab = (
    <>
      <Form.Item
        name="login"
        label={t('subscribers.form.login')}
        rules={[
          { required: true, message: t('subscribers.form.loginRequired') },
          { pattern: LOGIN_PATTERN, message: t('subscribers.form.loginPattern') },
        ]}
        // ⚠️ Login TAHRIRLANMAYDI: u RADIUS `username` ning o'zi
        // (`SessionControlResource` nishonni shu bo'yicha topadi).
        // O'zgartirilsa abonentning faol sessiyasi bazadagi yozuvdan
        // ajralib qolardi — ulanishi ishlayveradi, boshqarib esa
        // bo'lmaydi.
        extra={isCreate ? undefined : t('subscribers.form.loginLocked')}
      >
        <Input autoFocus={isCreate} autoComplete="off" disabled={!isCreate} />
      </Form.Item>

      {isCreate ? (
        <Form.Item
          name="password"
          label={t('subscribers.form.password')}
          rules={[
            { required: true, message: t('subscribers.form.passwordRequired') },
            { min: 4, max: 128, message: t('subscribers.form.passwordLength') },
          ]}
          // ⚠️ Parol FAQAT yaratishda. Tahrirlashda u alohida amal
          // (`subscriber.password.set` — xavfli huquq) va u yerda
          // parol ekranga hech qachon qaytarilmaydi.
          extra={t('subscribers.form.passwordHint')}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      ) : null}

      {showDisplayName ? (
        <Form.Item name="displayName" label={t('subscribers.form.displayName')}>
          <Input autoFocus={!isCreate} autoComplete="off" />
        </Form.Item>
      ) : null}

      <Form.Item
        name="phone"
        label={t('subscribers.form.phone')}
        // ⚠️ Bu maydon KONTAKT telefoni EMAS: SMS aynan
        // `subscriber.phone` ustunidan oqadi (ADR-031 §2) va u yerda
        // E.164 majburiy. Kontakt bo'limidagi raqamda esa format
        // majburlanmaydi — farq shu izohda aytiladi.
        extra={t('subscribers.form.phoneSms')}
        rules={[
          {
            validator: (_rule, value: string | undefined) => {
              const normalized = normalizePhone(value)
              if (normalized === null || PHONE_E164.test(normalized)) return Promise.resolve()
              return Promise.reject(new Error(t('subscribers.form.phonePattern')))
            },
          },
        ]}
      >
        <Input autoComplete="off" placeholder="+998901234567" inputMode="tel" />
      </Form.Item>

      <Form.Item
        name="kind"
        label={t('subscribers.form.kind')}
        rules={[{ required: true, message: t('subscribers.form.kindRequired') }]}
      >
        {/* ⚠️ Tahrirlashda ham OCHIQ qoladi: usul o'zgarishi haqiqiy
            holat (mijoz PPPoE dan statik IP ga o'tadi) va uni faqat
            admin qila oladigan qilib qo'yishga sabab yo'q — server
            `subscriber.update` bilan baribir qabul qiladi. */}
        <Select options={DELIVERY_METHODS.map((value) => ({ value, label: value }))} />
      </Form.Item>
    </>
  )

  const contactTab = (
    <>
      {/* ⛔ QULF — MA'LUMOTNI SAQLAB QOLADI, bezak emas. Ikkala
          muqobil ham raqamni YO'Q QILARDI (yuqoridagi `locked` izohi),
          shuning uchun ochilmaguncha kontakt tahriri yopiq qoladi.

          ⚠️ Ochish tugmasi FAQAT `contact.reveal` bilan chiziladi:
          huquqsiz sherikda u har bosishda 403 berardi. Huquq yo'q
          bo'lsa qulf o'z holicha qoladi va sabab aytiladi — bu
          avvalgi (2026-08-23) xulqning aynan o'zi. */}
      {locked ? (
        <Alert
          type="warning"
          showIcon
          message={t('subscribers.form.contactLocked')}
          description={
            canReveal ? (
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('phone.revealWarn')}
                </Typography.Text>
                <Space.Compact style={{ width: '100%' }}>
                  {/* ⛔ SABAB MAJBURIY: server usiz 400 beradi, lekin
                      so'rov UMUMAN ketmasligi kerak — har urinish
                      auditga tushadi va rad etilgani ham iz qoldirardi. */}
                  <Input
                    value={revealReason}
                    onChange={(e) => setRevealReason(e.target.value)}
                    placeholder={t('phone.revealReason')}
                    maxLength={REVEAL_REASON_MAX}
                  />
                  <Button
                    type="primary"
                    loading={revealing}
                    disabled={revealReason.trim() === ''}
                    onClick={doReveal}
                  >
                    {t('phone.reveal')}
                  </Button>
                </Space.Compact>
                {revealError ? (
                  <Typography.Text type="danger" style={{ fontSize: 12 }}>
                    {translateError(revealError, t)}
                  </Typography.Text>
                ) : null}
              </Space>
            ) : (
              t('subscribers.form.contactLockedNoPerm')
            )
          }
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {!isCreate && !contactQuery.isFetching && contact === null ? (
        <Alert
          type="info"
          showIcon
          message={t('subscribers.form.contactUnknown')}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {/* ⚠️ Barcha maydonni bo'shatish kontaktni O'CHIRMAYDI — bu
          `contactRequest()` dagi qarorning ekrandagi aksi. Aytilmasa
          operator «o'chirdim» deb o'ylab qolardi. */}
      {existing !== null && !locked ? (
        <Alert
          type="info"
          showIcon
          message={t('subscribers.form.contactKeepHint')}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {geoForbidden ? (
        <Alert
          type="warning"
          showIcon
          message={t('subscribers.form.geoForbidden')}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {geoTruncated ? (
        <Alert
          type="warning"
          showIcon
          message={t('subscribers.form.geoTruncated')}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        {t('subscribers.form.contactHint')}
      </Typography.Paragraph>

      {/* ⚠️ Ikki ustun FAQAT `md` dan keng ekranda: telefonda yonma-yon
          maydon har birini 150px ga toraytirardi. Kompyuterda esa bir
          ustun kontakt bo'limini behuda uzun qilardi. */}
      <Row gutter={[12, 0]}>
        <Col xs={24} md={12}>
          <Form.Item
            name="fullName"
            label={t('subscribers.form.contactFullName')}
            rules={[
              {
                max: CONTACT_LIMITS.fullName,
                message: t('subscribers.form.contactTooLong', { max: CONTACT_LIMITS.fullName }),
              },
            ]}
          >
            <Input autoComplete="off" disabled={contactDisabled} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="email"
            label={t('subscribers.form.contactEmail')}
            rules={[
              {
                max: CONTACT_LIMITS.email,
                message: t('subscribers.form.contactTooLong', { max: CONTACT_LIMITS.email }),
              },
              {
                validator: (_rule, value: string | undefined) => {
                  const trimmed = value?.trim() ?? ''
                  if (trimmed === '' || CONTACT_EMAIL_SHAPE.test(trimmed)) return Promise.resolve()
                  return Promise.reject(new Error(t('subscribers.form.contactEmailShape')))
                },
              },
            ]}
          >
            <Input autoComplete="off" inputMode="email" disabled={contactDisabled} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="contactPhone"
            label={t('subscribers.form.contactPhone')}
            // ⚠️ Format MAJBURLANMAYDI (egasi qarori): tizim
            // Afg'onistonda ham ishlaydi va niqob yoki `+998` talabi
            // mahalliy raqamni butunlay to'sib qo'yardi. Tekshiruv
            // faqat uzunlik va xavfli belgi — `ContactPhoneRule` dagi
            // bilan bir xil.
            extra={t('subscribers.form.contactPhoneFree')}
            rules={[{ validator: (_rule, value: string | undefined) => phoneRule(value, t) }]}
          >
            <Input autoComplete="off" inputMode="tel" disabled={contactDisabled} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="phoneAlt"
            label={t('subscribers.form.contactPhoneAlt')}
            dependencies={['contactPhone']}
            rules={[
              { validator: (_rule, value: string | undefined) => phoneRule(value, t) },
              {
                // ⚠️ `contact_phone_alt_needs_phone` va «ikki ustunda
                // bir xil raqam» — ikkalasi ham serverda 400 beradi.
                // Bu yerda ular maydon YONIDA aytiladi.
                validator: (_rule, value: string | undefined) => {
                  const alt = normalizePhone(value)
                  if (alt === null) return Promise.resolve()
                  // ⚠️ `getFieldValue` `any` qaytaradi; `getFieldsValue`
                  // esa formaning O'Z tipini beradi.
                  const main = normalizePhone(form.getFieldsValue().contactPhone)
                  if (main === null) {
                    return Promise.reject(
                      new Error(t('subscribers.form.contactPhoneAltNeedsPhone')),
                    )
                  }
                  if (main === alt) {
                    return Promise.reject(new Error(t('subscribers.form.contactPhoneAltSame')))
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input autoComplete="off" inputMode="tel" disabled={contactDisabled} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item name="regionId" label={t('subscribers.form.contactRegion')}>
            {/* ⚠️ `Select`, erkin matn EMAS (egasi qarori): viloyat
                alohida reestrda va formadan QO'SHIB bo'lmaydi. Erkin
                matn bitta viloyatni bazada bir necha imloda yozardi. */}
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              loading={regionQuery.isFetching}
              disabled={contactDisabled || geoForbidden}
              placeholder={t('common.empty')}
              options={regionOptions.map((option) => ({ value: option.id, label: option.name }))}
              // ⚠️ VILOYAT O'ZGARSA TUMAN TOZALANADI. Usiz nomuvofiq
              // juftlik saqlanardi: bazadagi kompozit FK uni rad etadi
              // va operator sababi ko'rinmaydigan xato olardi.
              onChange={() => form.setFieldValue('districtId', undefined)}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="districtId"
            label={t('subscribers.form.contactDistrict')}
            extra={
              regionId === undefined || regionId === ''
                ? t('subscribers.form.contactDistrictHint')
                : undefined
            }
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              loading={districtQuery.isFetching}
              // ⚠️ Viloyat tanlanmaguncha YOPIQ: ochiq qoldirilsa ro'yxat
              // bo'sh chiqardi va operator «tumanlar yo'q» deb o'ylardi.
              disabled={
                contactDisabled || geoForbidden || regionId === undefined || regionId === ''
              }
              placeholder={t('common.empty')}
              options={districtOptions.map((option) => ({ value: option.id, label: option.name }))}
            />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item
            name="address"
            label={t('subscribers.form.contactAddress')}
            rules={[
              {
                max: CONTACT_LIMITS.address,
                message: t('subscribers.form.contactTooLong', { max: CONTACT_LIMITS.address }),
              },
            ]}
          >
            <Input autoComplete="off" disabled={contactDisabled} />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item
            name="note"
            label={t('subscribers.form.contactNote')}
            rules={[
              {
                max: CONTACT_LIMITS.note,
                message: t('subscribers.form.contactTooLong', { max: CONTACT_LIMITS.note }),
              },
            ]}
          >
            <Input.TextArea rows={2} disabled={contactDisabled} />
          </Form.Item>
        </Col>
      </Row>
    </>
  )

  return (
    <Modal
      open={open}
      title={isCreate ? t('subscribers.form.createTitle') : t('subscribers.form.editTitle')}
      okText={savedId === null ? t('common.save') : t('subscribers.form.contactRetry')}
      cancelText={savedId === null ? t('common.cancel') : t('common.close')}
      confirmLoading={saving}
      onOk={() => form.submit()}
      onCancel={onClose}
      // ⚠️ Kompyuterda kengroq — kontakt bo'limi ikki ustunga bo'linadi.
      // Telefonda `undefined`: `styles/global.css` modalni to'liq ekran
      // qiladi va bu yerdagi son unga qarshi ishlardi.
      width={isMobile ? undefined : 760}
      // ⚠️ Kompyuterda tana O'ZI aylanadi, modal emas: aks holda uzun
      // forma «Saqlash» tugmasini ekran ostiga tushirib yuborardi.
      styles={isMobile ? undefined : { body: { maxHeight: 'calc(100vh - 240px)', overflowY: 'auto' } }}
      destroyOnHidden
    >
      {errorText ? (
        <Alert type="error" showIcon message={errorText} style={{ marginBottom: 12 }} />
      ) : null}

      {contactErrorText ? (
        <Alert
          type="warning"
          showIcon
          message={t('subscribers.form.contactFailed')}
          description={contactErrorText}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      <Form<FormValues>
        form={form}
        layout="vertical"
        onFinish={submit}
        // ⚠️ Xato YASHIRIN bo'limda bo'lsa, o'sha bo'lim OCHILADI.
        onFinishFailed={(info) => {
          const first = info.errorFields[0]?.name[0]
          setTab(
            first === 'profile' ? 'customer' : ACCESS_FIELDS.includes(String(first)) ? 'access' : 'contact',
          )
        }}
        requiredMark
      >
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            // ⚠️ `forceRender` MAJBURIY: ochilmagan bo'limdagi
            // `Form.Item` mount bo'lmasa uning tekshiruvi ham
            // ishlamaydi va noto'g'ri qiymat serverga jimgina ketardi.
            {
              key: 'customer',
              label: t('customer.profile.section'),
              children: <SubscriberProfileFields mode={isCreate ? 'create' : 'edit'} current={subscriber?.profile} />,
              forceRender: true,
            },
            { key: 'access', label: t('subscribers.form.tabAccess'), children: accessTab, forceRender: true },
            { key: 'contact', label: t('subscribers.form.tabContact'), children: contactTab, forceRender: true },
          ]}
        />
      </Form>
    </Modal>
  )
}

/** `ContactPhoneRule` ning panel tomondagi nusxasi — E.164 EMAS */
function phoneRule(value: string | undefined, t: (key: string, o?: Record<string, unknown>) => string) {
  const normalized = normalizePhone(value)
  if (normalized === null) return Promise.resolve()
  if (CONTACT_PHONE_FORBIDDEN.test(normalized)) {
    return Promise.reject(new Error(t('subscribers.form.contactPhoneForbidden')))
  }
  if (normalized.length < CONTACT_PHONE_MIN || normalized.length > CONTACT_PHONE_MAX) {
    return Promise.reject(
      new Error(
        t('subscribers.form.contactPhoneLength', {
          min: CONTACT_PHONE_MIN,
          max: CONTACT_PHONE_MAX,
        }),
      ),
    )
  }
  return Promise.resolve()
}
